<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { fly } from "svelte/transition";
	import { goto, yyyy } from "./store.svelte";

	import RegulatoryOversight from "./use-case/RegulatoryOversight.svelte";
	import ArcgisMigration from "./use-case/ArcgisMigration.svelte";
	import SmallTeams from "./use-case/SmallTeams.svelte";
	import FieldDocumentation from "./use-case/FieldDocumentation.svelte";
	import ReliabilityMetrics from "./use-case/ReliabilityMetrics.svelte";
	import OutageResponse from "./use-case/OutageResponse.svelte";
	import KnowledgeTransfer from "./use-case/KnowledgeTransfer.svelte";
	import VegetationManagement from "./use-case/VegetationManagement.svelte";
	import NetworkSnapshots from "./use-case/NetworkSnapshots.svelte";
	import EnergyBalance from "./use-case/EnergyBalance.svelte";
	import DataAccuracy from "./use-case/DataAccuracy.svelte";
	import SwitchValidation from "./use-case/SwitchValidation.svelte";

	let ready = false;
	let carouselEl: HTMLElement;

	const scrollCarousel = (direction: 'left' | 'right') => {
		if (!carouselEl) return;
		const scrollAmount = 340;
		carouselEl.scrollBy({
			left: direction === 'left' ? -scrollAmount : scrollAmount,
			behavior: 'smooth'
		});
	};

	const useCases = [
		{ slug: 'regulatory-oversight', name: 'Regulatory Oversight', subtitle: 'Independent verification of utility network claims', component: RegulatoryOversight },
		{ slug: 'small-teams', name: 'Small Teams', subtitle: 'Planning department capabilities without the planning department', component: SmallTeams },
		{ slug: 'field-documentation', name: 'Field Documentation', subtitle: 'Bridge the gap between your GIS and reality', component: FieldDocumentation },
		{ slug: 'reliability-metrics', name: 'Reliability Metrics', subtitle: 'Calculate SAIDI, SAIFI & reliability metrics', component: ReliabilityMetrics },
		{ slug: 'arcgis-migration', name: 'ArcGIS Migration', subtitle: 'Your path forward from ArcGIS Desktop', component: ArcgisMigration },
		{ slug: 'outage-response', name: 'Outage Response', subtitle: 'Faster restoration and better communication', component: OutageResponse },
		{ slug: 'knowledge-transfer', name: 'Knowledge Transfer', subtitle: 'Preserve institutional knowledge before it walks out the door', component: KnowledgeTransfer },
		{ slug: 'vegetation-management', name: 'Vegetation Management', subtitle: 'Prioritize trimming based on reliability impact', component: VegetationManagement },
		{ slug: 'network-snapshots', name: 'Network Snapshots', subtitle: 'Point-in-time versions for comparison and compliance', component: NetworkSnapshots },
		{ slug: 'energy-balance', name: 'Energy Balance', subtitle: 'Find where your energy is going and where it\'s disappearing', component: EnergyBalance },
		{ slug: 'data-accuracy', name: 'Data Accuracy', subtitle: 'Find the errors in your network data before they find you', component: DataAccuracy },
		{ slug: 'switch-validation', name: 'Switch Validation', subtitle: 'Validate switching sequences before anyone goes into the field', component: SwitchValidation },
	];

	let getSelectedComponent = () => {
		if (!yyyy.selectedUseCase) return null;
		const uc = useCases.find(u => u.slug === yyyy.selectedUseCase);
		return uc ? uc.component : null;
	};

	onMount(() => {
		ready = true;
	});

	onDestroy(() => {
		yyyy.logo.time = 877.5;
		yyyy.logo.usage = "wait";
	});
</script>

{#if ready}
	{#key yyyy.selectedUseCase}
	{#if yyyy.selectedUseCase}
		<a
			href="/use-cases"
			class="back-to-use-cases"
			onclick={(e) => { e.preventDefault(); goto('/use-cases'); }}
		>← Use Cases</a>
		{@const Component = getSelectedComponent()}
		{#if Component}
			<svelte:component this={Component} />
		{/if}
	{:else}
		<div class="use-case-landing" transition:fly|local={{ y: -200 }}>
			<h1 class="landing-title">Use Cases</h1>
			<p class="landing-subtitle">Click a use case to learn more</p>

			<div class="carousel-container">
				<button class="carousel-arrow left" onclick={() => scrollCarousel('left')}>&#8249;</button>
				<button class="carousel-arrow right" onclick={() => scrollCarousel('right')}>&#8250;</button>
				<div class="carousel" bind:this={carouselEl}>
					{#each useCases as useCase}
						<div
							class="carousel-item"
							onclick={() => goto(`/use-cases/${useCase.slug}`)}
							role="button"
							tabindex="0"
							onkeydown={(e) => e.key === 'Enter' && goto(`/use-cases/${useCase.slug}`)}
						>
							<div class="preview-wrapper">
								<div class="preview-content">
									<svelte:component this={useCase.component} />
								</div>
							</div>
							<div class="preview-label">{useCase.name}</div>
						</div>
					{/each}
				</div>
			</div>

		</div>
	{/if}
	{/key}
{/if}

<style>
	.back-to-use-cases {
		position: fixed;
		top: 15px;
		left: 15px;
		z-index: 10;
		color: #ff3e00;
		text-decoration: none;
		font-size: 0.9rem;
		font-weight: 500;
	}

	.back-to-use-cases:hover {
		text-decoration: underline;
	}

	.use-case-landing {
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		width: 100%;
		padding: 20px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		overflow-y: auto;
		z-index: 5;
	}

	.landing-title {
		color: #ff3e00;
		text-transform: uppercase;
		font-size: 3.5rem;
		font-family: lvwr-ubuntu-thin;
		text-align: center;
		margin: 0 0 8px 0;
	}

	.landing-subtitle {
		color: #666;
		font-size: 1em;
		margin: 0 0 20px 0;
	}

	.carousel-container {
		width: 100%;
		max-width: 100vw;
		overflow: hidden;
		position: relative;
	}

	.carousel-arrow {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		z-index: 10;
		background: transparent;
		border: none;
		width: 56px;
		height: 56px;
		font-size: 48px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #ff3e00;
		transition: all 0.2s ease;
	}

	.carousel-arrow:hover {
		color: #e63600;
		transform: translateY(-50%) scale(1.2);
	}

	.carousel-arrow.left {
		left: 10px;
	}

	.carousel-arrow.right {
		right: 10px;
	}


	.carousel {
		display: flex;
		gap: 20px;
		overflow-x: auto;
		scroll-snap-type: x mandatory;
		scroll-behavior: smooth;
		padding: 20px;
		-webkit-overflow-scrolling: touch;
		justify-content: left;
		margin-left: 60px;
		margin-right: 60px;
	}

	.carousel {
		scrollbar-width: none;
		-ms-overflow-style: none;
	}

	.carousel::-webkit-scrollbar {
		display: none;
	}

	.carousel-item {
		flex: 0 0 auto;
		scroll-snap-align: center;
		cursor: pointer;
		transition: transform 0.3s ease, box-shadow 0.3s ease;
	}

	.carousel-item:hover {
		transform: translateY(-5px);
	}

	.carousel-item:hover .preview-wrapper {
		box-shadow: 0 8px 24px rgba(255, 62, 0, 0.3);
	}

	.preview-wrapper {
		width: 360px;
		height: 480px;
		overflow: hidden;
		border-radius: 9px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		background: #fff;
		position: relative;
	}

	.preview-content {
		transform: scale(0.36);
		transform-origin: top left;
		width: 277.78%;
		height: 277.78%;
		pointer-events: none;
	}

	.preview-label {
		text-align: center;
		padding: 12px;
		font-weight: 600;
		color: #333;
		font-size: 0.95rem;
	}

</style>
